import L from 'leaflet';
import { cssRule } from 'typestyle';

// Shown until Esri's own credit of the layer arrives, and when it cannot be loaded
export const ESRI_IMAGERY_SOURCES =
  'Esri, Vantor, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS, AeroGRID, IGN, and the GIS User Community';
export const ESRI_LABELS_SOURCES =
  'Esri, TomTom, Garmin, FAO, NOAA, USGS, \u00a9 OpenStreetMap contributors, and the GIS User Community';

// Esri's attribution rules for interactive maps allow the whole credit ("Powered by Esri" and the data sources)
// behind an (i) button ("In a pop-up"). The mission-card maps are too small for the text, so only the (i) shows.
// The (i) of the credit: one SVG in every Alpha app, so fonts and button resets cannot change it
const INFO_ICON =
  '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">' +
  '<circle cx="7" cy="7" r="6.25" fill="none" stroke="currentColor" stroke-width="1.25"/>' +
  '<circle cx="7" cy="4.25" r="0.95" fill="currentColor"/>' +
  '<rect x="6.3" y="6" width="1.4" height="4.6" rx="0.7" fill="currentColor"/></svg>';

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The credit with one sources line per Esri layer on the map: `Imagery: ...` and, if given, `Labels: ...`. */
export const esriAttribution = (imagery: string, labels?: string) =>
  '<span class="esri-credit">' +
  '<span class="esri-credit-sources" hidden>' +
  '<a href="https://www.esri.com" target="_blank" rel="noreferrer">Powered by Esri</a>' +
  `<span class="esri-credit-list">Imagery: ${escapeHtml(imagery)}</span>` +
  (labels === undefined ? '' : `<span class="esri-credit-list">Labels: ${escapeHtml(labels)}</span>`) +
  '</span>' +
  '<span class="esri-credit-line">' +
  `<button type="button" class="esri-credit-info" aria-label="Map credits" aria-expanded="false">${INFO_ICON}</button>` +
  '</span></span>';

// Esri's credit of a layer ("copyrightText" of its service metadata), loaded once per page and service.
// `text` is set once it has loaded.
type EsriCopyright = { text?: string; request: Promise<string | undefined> };
const esriCopyrights = new Map<string, EsriCopyright>();
let esriCopyrightWarned = false;

/** Service metadata URL of an Esri tile URL template: the template without `/tile/{z}/{y}/{x}`, as JSON. */
export const esriMetadataUrl = (tileUrl: string) => {
  const [path, query] = tileUrl.split('?');
  return `${path.replace(/\/tile\/\{z\}\/\{y\}\/\{x\}$/, '')}?f=json${query ? `&${query}` : ''}`;
};

/** Esri's credit of the tile layer without its "Source:"/"Sources:" prefix; undefined if it cannot be loaded. */
export const loadEsriCopyright = (tileUrl: string): EsriCopyright => {
  const url = esriMetadataUrl(tileUrl);
  const cached = esriCopyrights.get(url);
  if (cached) return cached;

  const copyright: EsriCopyright = {
    request: fetch(url)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((metadata: { copyrightText?: unknown; error?: unknown } | null): string => {
        const text =
          typeof metadata?.copyrightText === 'string'
            ? metadata.copyrightText.replace(/^\s*Sources?:\s*/i, '').trim()
            : '';
        if (!text) throw new Error(metadata?.error ? JSON.stringify(metadata.error) : 'no copyrightText');
        return (copyright.text = text);
      })
      .catch((error): undefined => {
        // Esri's credit is optional: one warning per page, the built-in sources stay
        if (!esriCopyrightWarned) console.warn('Esri map credit: using the built-in sources', error);
        esriCopyrightWarned = true;
        return undefined;
      }),
  };
  esriCopyrights.set(url, copyright);
  return copyright;
};

/** An Esri tile layer and its tile URL template */
export type EsriLayer = { layer: L.TileLayer; url: string };

/**
 * Puts the Esri credit on the imagery layer: the built-in sources first, Esri's own credit of each layer once
 * loaded. The Labels line shows only while the labels layer is on the map.
 */
export const showEsriCredit = (imagery: EsriLayer, labels?: EsriLayer) => {
  const imageryCopyright = loadEsriCopyright(imagery.url);
  const labelsCopyright = labels && loadEsriCopyright(labels.url);
  let labelsOn = false;

  const render = () => {
    const { options } = imagery.layer;
    const attribution = esriAttribution(
      imageryCopyright.text ?? ESRI_IMAGERY_SOURCES,
      labelsOn ? labelsCopyright?.text ?? ESRI_LABELS_SOURCES : undefined,
    );
    if (attribution === options.attribution) return;
    // The attribution control shows the attribution strings of the layers on the map: swap the old one for the new
    const control = (imagery.layer as unknown as { _map?: L.Map })._map?.attributionControl;
    if (options.attribution) control?.removeAttribution(options.attribution);
    options.attribution = attribution;
    control?.addAttribution(attribution);
  };

  labels?.layer.on('add', () => {
    labelsOn = true;
    render();
  });
  labels?.layer.on('remove', () => {
    labelsOn = false;
    render();
  });
  render();
  void imageryCopyright.request.then(render);
  void labelsCopyright?.request.then(render);
};

// Pilot-app maps show only the (i) of the Esri credit: a gray icon on the map, no box behind it
cssRule('.leaflet-container .leaflet-control-container .leaflet-control-attribution', {
  padding: '0 6px 4px',
  background: 'transparent',
  color: '#323232',
  font: '12px/16px "Avenir Next W00", "Helvetica Neue", Helvetica, Arial, sans-serif',
  textAlign: 'right',
});
cssRule('.leaflet-container .leaflet-control-container .leaflet-control-attribution a', {
  color: 'inherit',
  textDecoration: 'none',
  $nest: { '&:hover, &:focus': { textDecoration: 'underline' } },
});
// The sources sit in the same box, above the credit line, so the box grows upwards when they open
cssRule('.esri-credit', { display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' });
cssRule('.esri-credit-line', { display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' });
// Fully self-contained: the app's global `button` reset and UA button styles must not change it
cssRule('.esri-credit-info', {
  appearance: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
  boxSizing: 'border-box',
  width: 14,
  height: 14,
  margin: 0,
  padding: 0,
  border: 0,
  background: 'transparent',
  color: '#D9D9D9',
  filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.6))',
  cursor: 'pointer',
  verticalAlign: 'middle',
  $nest: {
    '&:hover': { opacity: 0.7 },
    '&:focus-visible': { outline: '1px solid currentColor', outlineOffset: 1 },
  },
});
// The credits open as a small pop-up above the (i). While open it is moved to <body> and fixed to the viewport,
// so nothing on the card (clipped map box, expand button, "Updated" badge) can cut or cover it; setOpen() places it.
cssRule('.esri-credit-sources', {
  position: 'fixed',
  // Above the expanded (full-screen) map, which sits at 9999
  zIndex: 10000,
  display: 'block',
  boxSizing: 'border-box',
  overflowY: 'auto',
  padding: '8px 12px',
  borderRadius: 8,
  background: '#fff',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.25)',
  color: '#323232',
  font: '12px/16px "Avenir Next W00", "Helvetica Neue", Helvetica, Arial, sans-serif',
  textAlign: 'left',
  whiteSpace: 'normal',
  userSelect: 'text',
  cursor: 'text',
});
cssRule('.esri-credit-sources[hidden]', { display: 'none' });
cssRule('.esri-credit-sources a', {
  display: 'block',
  marginBottom: 2,
  color: 'inherit',
  textDecoration: 'none',
  $nest: { '&:hover, &:focus': { textDecoration: 'underline' } },
});
cssRule('.esri-credit-list', { display: 'block' });

// Opening one map's credits closes the others (clicks on a map's credit never reach the document)
const OPEN_EVENT = 'esri-credit-open';

/**
 * Makes the (i) button of {@link esriAttribution} toggle the credits (closes on a second click,
 * Esc, a click anywhere else or when another map's credits open).
 */
export const enableEsriCredit = (map: L.Map) => {
  const container = map.getContainer();
  const button = () => container.querySelector<HTMLButtonElement>('.esri-credit-info');
  // The open pop-up, moved out of the map to <body>
  let opened: HTMLElement | null = null;
  const inPopup = (target: EventTarget | null) =>
    target instanceof Node && (!!opened?.contains(target) || !!(target as Element).closest?.('.esri-credit'));

  const setOpen = (open: boolean) => {
    const sources = opened ?? container.querySelector<HTMLElement>('.esri-credit-sources');
    if (!button() || !sources) return;
    button()!.setAttribute('aria-expanded', String(open));
    sources.hidden = !open;
    if (!open) {
      // Back into the credit, or dropped if the attribution was re-rendered meanwhile
      const credit = container.querySelector('.esri-credit');
      if (credit && !credit.querySelector('.esri-credit-sources')) credit.prepend(sources);
      else sources.remove();
      opened = null;
      return;
    }
    opened = sources;
    document.body.appendChild(sources);
    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: container }));
    // Above the (i), right edges aligned, kept 8px inside the viewport
    const icon = button()!.getBoundingClientRect();
    const width = Math.min(280, window.innerWidth - 16);
    sources.style.width = `${width}px`;
    sources.style.left = `${Math.max(8, Math.min(icon.right - width, window.innerWidth - width - 8))}px`;
    sources.style.bottom = `${window.innerHeight - icon.top + 6}px`;
    sources.style.maxHeight = `${icon.top - 14}px`;
  };
  // Tracked by the moved pop-up, not the button: the attribution can re-render (new button) while it is open
  const isOpen = () => opened !== null;

  const onCreditClick = (event: MouseEvent) => {
    const target = event.target as Element;
    if (!target.closest('.esri-credit')) return;
    // Keep credit clicks away from the map and from click handlers around it (e.g. the mission card).
    event.stopPropagation();
    if (target.closest('.esri-credit-info')) setOpen(!isOpen());
  };
  const onOutsideClick = (event: MouseEvent) => {
    if (isOpen() && !inPopup(event.target)) setOpen(false);
  };
  const onOtherOpen = (event: Event) => {
    if (isOpen() && (event as CustomEvent).detail !== container) setOpen(false);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen()) setOpen(false);
  };

  // The pop-up is fixed to the viewport, so it closes when the page scrolls or resizes under it
  const onScrollOrResize = (event: Event) => {
    if (isOpen() && !inPopup(event.target)) setOpen(false);
  };

  container.addEventListener('click', onCreditClick);
  document.addEventListener('click', onOutsideClick);
  document.addEventListener(OPEN_EVENT, onOtherOpen);
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);
  map.once('unload', () => {
    container.removeEventListener('click', onCreditClick);
    document.removeEventListener('click', onOutsideClick);
    document.removeEventListener(OPEN_EVENT, onOtherOpen);
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
    opened?.remove();
  });
};
